import XPLORPAY_CONFIG, { getPublicKey } from './XplorPayConfig';

/**
 * XplorPay SDK Wrapper
 *
 * Handles loading, initialization, and interaction with the ClearentSDK
 * for PCI-compliant payment form handling.
 */
class XplorPaySDKWrapper {
  constructor() {
    this.sdkLoaded = false;
    this.sdkInitialized = false;
    this.initPromise = null;
    this.currentMode = 'card'; // 'card' or 'ach'
  }

  /**
   * Load the XplorPay SDK script dynamically
   */
  loadSDKScript() {
    if (this.sdkLoaded) {
      return Promise.resolve();
    }

    // Check if already in DOM
    const existingScript = document.querySelector(`script[src="${XPLORPAY_CONFIG.sdkUrl}"]`);
    if (existingScript) {
      this.sdkLoaded = true;
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = XPLORPAY_CONFIG.sdkUrl;
      script.async = true;

      script.onload = () => {
        this.sdkLoaded = true;
        console.log('XplorPay SDK loaded successfully');
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load XplorPay SDK script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize the SDK with merchant credentials
   * @param {Object} options - Additional SDK options
   * @param {boolean} enableAch - Whether to show ACH fields
   */
  async init(options = {}, enableAch = false) {
    // If already initialized with same mode, return
    if (this.sdkInitialized && this.currentMode === (enableAch ? 'ach' : 'card')) {
      return Promise.resolve();
    }

    // If initialization in progress, wait for it
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        // Load SDK script first
        await this.loadSDKScript();

        // Get merchant's public key
        const publicKey = getPublicKey();
        if (!publicKey) {
          throw new Error('XplorPay public key not configured for this merchant');
        }

        // Wait for SDK to be available on window
        if (!window.ClearentSDK) {
          throw new Error('ClearentSDK not found on window object');
        }

        // Build initialization config
        const initConfig = {
          baseUrl: XPLORPAY_CONFIG.baseUrl,
          pk: publicKey,
          paymentFormId: XPLORPAY_CONFIG.paymentFormId,
          enableAch: enableAch,
          ...XPLORPAY_CONFIG.defaultOptions,
          styles: XPLORPAY_CONFIG.styles,
          ...options,
        };

        // Initialize the SDK
        window.ClearentSDK.init(initConfig);

        this.sdkInitialized = true;
        this.currentMode = enableAch ? 'ach' : 'card';
        console.log(`XplorPay SDK initialized (mode: ${this.currentMode})`);

      } catch (error) {
        console.error('XplorPay SDK initialization failed:', error);
        this.initPromise = null;
        throw error;
      }
    })();

    return this.initPromise;
  }

  /**
   * Initialize for card payments
   */
  async initForCard(options = {}) {
    return this.init(options, false);
  }

  /**
   * Initialize for ACH payments
   */
  async initForAch(options = {}) {
    return this.init(options, true);
  }

  /**
   * Get payment token from the hosted form
   * Returns JWT token and card/account info (last 4 only)
   *
   * @returns {Promise<{jwt: string, lastFour: string, cardType?: string, expDate?: string}>}
   */
  async getPaymentToken() {
    if (!this.sdkInitialized) {
      throw new Error('XplorPay SDK not initialized. Call init() first.');
    }

    if (!window.ClearentSDK || !window.ClearentSDK.getPaymentToken) {
      throw new Error('ClearentSDK.getPaymentToken not available');
    }

    return new Promise((resolve, reject) => {
      // Set up success callback
      window.ClearentTokenSuccess = (raw, json) => {
        try {
          const response = typeof json === 'string' ? JSON.parse(json) : json;

          // Extract token info from response
          // Response format: { code: "200", status: "success", payload: { mobile-jwt: { jwt: "...", last-four: "1234" } } }
          const payload = response.payload || {};
          const mobileJwt = payload['mobile-jwt'] || {};

          const result = {
            jwt: mobileJwt.jwt || mobileJwt,
            lastFour: mobileJwt['last-four'] || payload['last-four-digits'],
            cardType: payload['card-type'],
            expDate: payload['exp-date'],
          };

          console.log('XplorPay token received:', { lastFour: result.lastFour, cardType: result.cardType });
          resolve(result);
        } catch (e) {
          console.error('Error parsing XplorPay token response:', e);
          reject(new Error('Failed to parse payment token response'));
        }
      };

      // Set up error callback
      window.ClearentTokenError = (raw, json) => {
        console.error('XplorPay token error:', json);
        const response = typeof json === 'string' ? JSON.parse(json) : json;
        const message = response?.payload?.error?.message || response?.message || 'Payment validation failed';
        reject(new Error(message));
      };

      // Request the token
      window.ClearentSDK.getPaymentToken();
    });
  }

  /**
   * Reset the SDK (destroys and reinitializes the iframe)
   */
  reset() {
    if (window.ClearentSDK && window.ClearentSDK.reset) {
      window.ClearentSDK.reset();
    }
    this.sdkInitialized = false;
    this.initPromise = null;
  }

  /**
   * Cleanup SDK resources
   */
  cleanup() {
    // Remove callbacks
    if (window.ClearentTokenSuccess) {
      delete window.ClearentTokenSuccess;
    }
    if (window.ClearentTokenError) {
      delete window.ClearentTokenError;
    }

    this.reset();
  }

  /**
   * Check if SDK is ready
   */
  isReady() {
    return this.sdkLoaded && this.sdkInitialized;
  }
}

// Export singleton instance
const XplorPaySDK = new XplorPaySDKWrapper();
export default XplorPaySDK;
